import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import {
  AttentionSeverityPlate,
  getAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IAttentionPartnerOverview, IAttentionReminderRoute } from '../../../../lib'
import { selectCockpitUserAccess } from '../../../../model/selectors'
import type { IAttentionPartnerOwners } from '../../../../model/selectors/selectCockpitAttentionLevel'
import {
  useAttentionPendingPbmReplacements,
  useCanReplaceAttentionPbm,
} from '../../hooks/useAttentionPbmReplacement'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import { AttentionPartnerLink } from '../attentionPartnerLink'
import { AttentionPbmName, AttentionPbmReplacePopup } from '../attentionPbmReplacement'
import { AttentionReminderButton } from '../attentionReminderButton'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen

/* No status is picked on the partner's panel, so its PBMs get the summary of everything it holds open. */
const PBM_SUMMARY_ROUTE: IAttentionReminderRoute = { bitValue: null, actionId: 'remindPbm' }

interface IAttentionFullscreenPartnerPanelProps {
  overview: IAttentionPartnerOverview
  owners?: IAttentionPartnerOwners
  onClose: () => void
}

/**
 * The partner picked in the table, beside it: every status it holds open, as the tile's own cards
 * draw them, and the PBMs who can close them. The table under it stays where it was.
 */
export const AttentionFullscreenPartnerPanel = ({
  overview,
  owners,
  onClose,
}: IAttentionFullscreenPartnerPanelProps) => {
  const t = useSafetyTranslation()
  const text = useAttentionStatusesText()
  const { partner, open } = overview
  const remindList = useMemo(
    () => ({
      partners: [{ partnerId: partner.partnerId, statuses: open.map(({ bitValue }) => bitValue) }],
    }),
    [open, partner.partnerId]
  )
  const { role } = useSelector(selectCockpitUserAccess)
  const statusRole = getAttentionStatusRole(role)
  const pbms = owners?.pbms ?? []
  const canReplace = useCanReplaceAttentionPbm()
  const getPending = useAttentionPendingPbmReplacements([partner.partnerId], canReplace)

  return (
    <aside className={styles.partnerPanel} aria-label={partner.partnerName}>
      <div className={styles.partnerPanelHead}>
        <span className={styles.partnerPanelName}>{partner.partnerName}</span>
        <AttentionPartnerLink
          partnerId={partner.partnerId}
          partnerName={partner.partnerName}
          className={styles.linkChip}
        />
        <button
          type="button"
          className={styles.partnerPanelClose}
          aria-label={t(KEYS.close)}
          onClick={onClose}
        >
          <i className="kbase kbase-close" aria-hidden="true" />
        </button>
      </div>
      <span className={styles.partnerPanelSub}>{partner.country}</span>

      {open.length === 0 ? (
        <p className={styles.empty}>
          <Translation span text={KEYS.allClear} />
        </p>
      ) : (
        <div className={styles.partnerPanelStatuses}>
          {open.map((status) => (
            <div key={status.bitValue} className={styles.panelStatus}>
              <AttentionSeverityPlate severity={status.severity} className={styles.panelPlate}>
                !
              </AttentionSeverityPlate>
              <span className={styles.panelStatusText}>
                <span>{text.statusTitleParts(status, 1).text}</span>
                <span className={styles.panelStatusSeverity}>
                  <Translation span {...text.phrase.severityName(status.severity)} />
                </span>
              </span>
              <span className={styles.panelStatusAction}>
                <Translation span {...text.phrase.actionLabel(status.bitValue, statusRole)} />
              </span>
            </div>
          ))}
        </div>
      )}

      {pbms.length > 0 && (
        <div className={styles.partnerPanelOwners}>
          <span className={styles.panelTitle}>
            <Translation span text={KEYS.columns.pbm} />
          </span>
          {pbms.map(({ ownerId, ownerName }) => {
            const pending = getPending(partner.partnerId, ownerId)

            return (
              <div key={ownerId} className={`${styles.owner} ${styles.ownerSpread}`}>
                <AttentionPbmName name={ownerName} pending={pending} className={styles.ownerName} />
                <span className={styles.ownerActions}>
                  {canReplace && !pending && (
                    <AttentionPbmReplacePopup
                      pbm={{ email: ownerId, name: ownerName }}
                      partners={[partner]}
                      assignedEmails={pbms.map(({ ownerId: email }) => email)}
                    />
                  )}
                  <AttentionReminderButton
                    reminder={{ email: ownerId, name: ownerName }}
                    route={PBM_SUMMARY_ROUTE}
                    list={remindList}
                    filled
                  />
                </span>
              </div>
            )
          })}
        </div>
      )}
    </aside>
  )
}

AttentionFullscreenPartnerPanel.displayName = 'AttentionFullscreenPartnerPanel'
